import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { buildOrderStatusMap } from "../../utils/orderStatus";
import CSSelect from "../../components/CSSelect";

// 마이페이지 팔레트에 맞춘 차분한 상태색 (관리자 화면은 utils/orderStatus 그대로)
const STATUS_COLORS = {
  pending: "#C8813F",
  confirmed: "#8A6A4F",
  printing: "#A0673A",
  printed: "#6F8A5B",
  shipping: "#5F7F86",
  delivered: "#7C6655",
  cancelled: "#B4543A",
};

// 진행 단계 (취소 제외)
const STEPS = ["pending", "confirmed", "printing", "printed", "shipping", "delivered"];

const API = process.env.REACT_APP_API_URL || "";

// 모듈 레벨 알림 헬퍼 (자식 컴포넌트도 사용)
let _showAlert = () => {};
function notify(message, type = "info") {
  _showAlert({ message, type });
}

export default function MyPageOrders() {
  const { t, i18n } = useTranslation();
  const STATUS_MAP = buildOrderStatusMap(t);
  const { user } = useContext(AuthContext);
  const [alertModal, setAlertModal] = useState(null); // {message, type}
  // 알림 모달 트리거 등록
  useEffect(() => {
    _showAlert = (a) => setAlertModal(a);
    return () => { _showAlert = () => {}; };
  }, []);
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [returnsByOrder, setReturnsByOrder] = useState({});

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      fetch(`${API}/api/my-orders?user_id=${user.id}`).then((r) => r.json()),
      fetch(`${API}/api/my-returns?user_id=${user.id}`).then((r) => r.json()),
    ])
      .then(([orderData, returnData]) => {
        const list = Array.isArray(orderData) ? orderData : [];
        setOrders(list);
        setFiltered(list);
        // 주문별 진행 중인 신청 매핑 (가장 최근 1건만)
        const map = {};
        if (Array.isArray(returnData)) {
          for (const r of returnData) {
            if (!map[r.order_id]) map[r.order_id] = r;
          }
        }
        setReturnsByOrder(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    let list = orders;
    if (dateFrom) list = list.filter((o) => o.created_at >= dateFrom);
    if (dateTo) list = list.filter((o) => o.created_at <= dateTo + "T23:59:59");
    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      list = list.filter((o) => (o.order_number || "").toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [dateFrom, dateTo, searchValue, orders]);

  async function loadDetail(id) {
    if (selectedId === id) { setSelectedId(null); return; }
    try {
      const res = await fetch(`${API}/api/orders/${id}`);
      const data = await res.json();
      setDetail(data);
      setSelectedId(id);
    } catch { /* ignore */ }
  }

  function reload() {
    if (!user?.id) return;
    Promise.all([
      fetch(`${API}/api/my-orders?user_id=${user.id}`).then((r) => r.json()),
      fetch(`${API}/api/my-returns?user_id=${user.id}`).then((r) => r.json()),
    ])
      .then(([orderData, returnData]) => {
        const list = Array.isArray(orderData) ? orderData : [];
        setOrders(list);
        setFiltered(list);
        const map = {};
        if (Array.isArray(returnData)) {
          for (const r of returnData) {
            if (!map[r.order_id]) map[r.order_id] = r;
          }
        }
        setReturnsByOrder(map);
      })
      .catch(console.error);
  }

  // 모달 상태
  const [cancelModal, setCancelModal] = useState(null); // {order, type: 'instant'|'request'}
  const [returnModal, setReturnModal] = useState(null); // {order, type: 'return'|'exchange'}
  const [addressModal, setAddressModal] = useState(null); // order

  async function confirmInstantCancel(order, reason) {
    try {
      const res = await fetch(`${API}/api/orders/${order.id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (res.ok) {
        notify(t("mypage.orders.cancelSuccess"), "success");
        setCancelModal(null);
        reload();
      } else {
        notify(data.error || t("mypage.orders.cancelFailed"), "error");
      }
    } catch (e) {
      notify(t("mypage.orders.cancelFailed"), "error");
    }
  }

  return (
    <div className="mypage-card mp-orders">
      <h2>{t("mypage.orders.title")}</h2>
      <p className="mypage-desc">{t("mypage.orders.desc")}</p>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#7C6655" }}>{t("mypage.common.loading")}</div>
      ) : orders.length === 0 ? (
        <div className="mp-orders-empty">
          <p>{t("mypage.orders.empty")}</p>
        </div>
      ) : (
        <>
      <div className="mp-orders-filter">
        <label>
          {t("mypage.orders.dateFrom")}
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label>
          {t("mypage.orders.dateTo")}
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label>
          {t("mypage.orders.orderNumber")}
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t("mypage.orders.orderNumber")}
            style={{ padding: "8px 12px", border: "1px solid #E7DAC8", borderRadius: 6, fontSize: 13 }}
          />
        </label>
        {(dateFrom || dateTo || searchValue) && (
          <button className="mp-filter-reset" onClick={() => { setDateFrom(""); setDateTo(""); setSearchValue(""); }}>{t("mypage.orders.reset")}</button>
        )}
        <span className="mp-filter-count">{filtered.length}{t("mypage.orders.countSuffix")}</span>
      </div>
      <div className="mp-orders-list">
        {filtered.map((o) => {
          const isExpanded = selectedId === o.id;
          const isCancelled = o.status === "cancelled";
          const stepIdx = STEPS.indexOf(o.status);
          const totalAmount = (o.total_price || 0) + (o.shipping_cost || 0);
          const trackUrl = o.tracking_number
            ? (o.shipping_type === "international"
              ? `https://t.17track.net/en#nums=${o.tracking_number}`
              : `https://trace.cjlogistics.com/web/detail.jsp?slipno=${o.tracking_number}`)
            : null;

          // 신청 상태
          const activeReq = returnsByOrder[o.id];
          const hasActiveReq = activeReq && ["pending", "approved", "collecting", "collected"].includes(activeReq.status);
          const isRejected = activeReq && activeReq.status === "rejected";
          const isCompleted = activeReq && activeReq.status === "completed";
          const isWithdrawn = activeReq && activeReq.status === "withdrawn";
          const reqType = activeReq?.request_type || activeReq?.type;
          const reqTypeLabel = reqType === "cancel" ? t("mypage.orders.cancelRequest")
                              : reqType === "exchange" ? t("mypage.orders.exchangeRequest")
                              : t("mypage.orders.returnRequest");

          return (
            <div key={o.id} className={`mp-order-card ${isExpanded ? "expanded" : ""}`}>
              {/* 상단 헤더: 주문번호 / 날짜 / 상태 */}
              <div className="mp-order-header" onClick={() => loadDetail(o.id)}>
                <div className="mp-order-meta">
                  <div className="mp-order-date-row">
                    <span className="mp-order-date-big">
                      {new Date(o.created_at).toLocaleDateString(i18n.language)}
                    </span>
                    <span className="mp-order-num-small">
                      {t("mypage.orders.orderNumber")} {o.order_number || "-"}
                    </span>
                  </div>
                </div>
                <div className="mp-order-right">
                  {hasActiveReq && (
                    <span className="mp-req-badge mp-req-pending">
                      {reqTypeLabel} {t("mypage.orders.reqInProgress")}
                    </span>
                  )}
                  {isRejected && (
                    <span className="mp-req-badge mp-req-rejected">
                      {reqTypeLabel} {t("mypage.orders.reqRejected")}
                    </span>
                  )}
                  {isCompleted && (
                    <span className="mp-req-badge" style={{ background: "#6F8A5B1F", color: "#5B7349", border: "1px solid #6F8A5B33" }}>
                      {reqTypeLabel} {t("mypage.orders.reqCompleted")}
                    </span>
                  )}
                  {isWithdrawn && (
                    <span className="mp-req-badge" style={{ background: "#9C8B7C1F", color: "#7C6655", border: "1px solid #9C8B7C33" }}>
                      {reqTypeLabel} {t("mypage.orders.reqWithdrawn")}
                    </span>
                  )}
                  <span
                    className="mp-order-status-badge"
                    style={{
                      background: `${STATUS_COLORS[o.status]}1F`,
                      color: STATUS_COLORS[o.status],
                      border: `1px solid ${STATUS_COLORS[o.status]}33`,
                    }}
                  >
                    {t(`mypage.orders.status.${o.status}`, STATUS_MAP[o.status] || o.status)}
                  </span>
                  <span className="mp-order-arrow">{isExpanded ? "▲" : "▼"}</span>
                </div>
              </div>

              {/* 신청 안내 박스 */}
              {(hasActiveReq || isRejected || isCompleted || isWithdrawn) && (
                <div className={`mp-req-notice ${
                  hasActiveReq ? "mp-req-notice-pending"
                  : isRejected ? "mp-req-notice-rejected"
                  : isCompleted ? "mp-req-notice-completed"
                  : "mp-req-notice-withdrawn"
                }`}>
                  <span className="mp-req-icon">{hasActiveReq ? "⏳" : isRejected ? "✕" : isCompleted ? "✓" : "↩"}</span>
                  <div className="mp-req-text">
                    <strong>
                      {hasActiveReq
                        ? t("mypage.orders.reqInProgressNotice", { type: reqTypeLabel })
                        : isRejected
                        ? t("mypage.orders.reqRejectedNotice", { type: reqTypeLabel })
                        : isCompleted
                        ? t("mypage.orders.reqCompletedNotice", { type: reqTypeLabel })
                        : t("mypage.orders.reqWithdrawnNotice", { type: reqTypeLabel })}
                    </strong>
                    <span>{t("mypage.orders.reqSeeDetails")}</span>
                    {isCompleted && (reqType === "cancel" || reqType === "return" || reqType === "refund") && (
                      <span style={{ color: "#7C6655", fontSize: 12 }}>{t("mypage.orders.refundProcessingNotice")}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="mp-req-link-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof window !== "undefined") {
                        // 부모 컴포넌트에 탭 변경 알림
                        const event = new CustomEvent("mypage:changeTab", { detail: "returns" });
                        window.dispatchEvent(event);
                      }
                    }}
                  >
                    {t("mypage.orders.goToReturns")} →
                  </button>
                </div>
              )}

              {/* 진행 단계 바 (취소되지 않은 경우만) */}
              {!isCancelled && (
                <div className="mp-order-steps">
                  {STEPS.map((step, i) => {
                    const done = stepIdx >= i;
                    const current = stepIdx === i;
                    return (
                      <div key={step} className={`mp-step ${done ? "done" : ""} ${current ? "current" : ""}`}>
                        <div className="mp-step-circle">{i + 1}</div>
                        <div className="mp-step-label">
                          {t(`mypage.orders.status.${step}`, STATUS_MAP[step])}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 본문 요약: 항목 수 / 총수량 / 금액 */}
              {(() => {
                const items = Array.isArray(o.items) ? o.items : [];
                const totalQty = items.reduce((s, it) => s + (it.quantity || 0), 0);
                return (
                  <div className="mp-order-summary-box" onClick={() => loadDetail(o.id)}>
                    <div className="mp-order-summary-row">
                      <div className="mp-summary-item">
                        <span className="mp-summary-label">{t("mypage.orders.itemCount", "항목")}</span>
                        <span className="mp-summary-value">{items.length}</span>
                      </div>
                      <div className="mp-summary-item">
                        <span className="mp-summary-label">{t("mypage.orders.quantity")}</span>
                        <span className="mp-summary-value">{totalQty}</span>
                      </div>
                      <div className="mp-summary-item">
                        <span className="mp-summary-label">{t("mypage.orders.totalPaid")}</span>
                        <span className="mp-summary-value mp-summary-price">
                          {totalAmount > 0 ? `${totalAmount.toLocaleString()}${t("mypage.orders.currencyWon")}` : "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* === 액션 버튼 영역 === */}
              {(!hasActiveReq && !isCompleted) && (() => {
                const buttons = [];
                if (o.status === "pending") {
                  buttons.push(
                    <button key="addr" className="mp-action-btn mp-action-secondary" onClick={(e) => { e.stopPropagation(); setAddressModal(o); }}>
                      {t("mypage.orders.editShipping")}
                    </button>
                  );
                  buttons.push(
                    <button key="cancel" className="mp-action-btn mp-action-danger" onClick={(e) => { e.stopPropagation(); setCancelModal({ order: o, type: "instant" }); }}>
                      {t("mypage.orders.cancelOrder")}
                    </button>
                  );
                } else if (["confirmed", "printing", "printed"].includes(o.status)) {
                  buttons.push(
                    <button key="cancel-req" className="mp-action-btn mp-action-secondary" onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(new CustomEvent("mypage:changeTab", { detail: "returns" }));
                      window.dispatchEvent(new CustomEvent("mypage:openReturnForm", { detail: { type: "cancel", orderId: o.id } }));
                    }}>
                      {t("mypage.orders.cancelRequest")}
                    </button>
                  );
                } else if (o.status === "delivered") {
                  buttons.push(
                    <button key="return" className="mp-action-btn mp-action-secondary" onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(new CustomEvent("mypage:changeTab", { detail: "returns" }));
                      window.dispatchEvent(new CustomEvent("mypage:openReturnForm", { detail: { type: "refund", orderId: o.id } }));
                    }}>
                      {t("mypage.orders.returnRequest")}
                    </button>
                  );
                  buttons.push(
                    <button key="exchange" className="mp-action-btn mp-action-secondary" onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(new CustomEvent("mypage:changeTab", { detail: "returns" }));
                      window.dispatchEvent(new CustomEvent("mypage:openReturnForm", { detail: { type: "exchange", orderId: o.id } }));
                    }}>
                      {t("mypage.orders.exchangeRequest")}
                    </button>
                  );
                }
                return buttons.length > 0 ? (
                  <div className="mp-order-actions">{buttons}</div>
                ) : null;
              })()}


              {/* 배송 추적 (운송장번호 있을 때) */}
              {o.tracking_number && (
                <div className="mp-order-tracking">
                  <div className="mp-tracking-info">
                    <span className="mp-tracking-icon">📦</span>
                    <div>
                      <div className="mp-tracking-label">{t("mypage.orders.trackingNumber")}</div>
                      <div className="mp-tracking-num">{o.tracking_number}</div>
                    </div>
                  </div>
                  <a
                    href={trackUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mp-track-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("mypage.orders.trackShipment")} →
                  </a>
                </div>
              )}

              {/* 상세 펼침 */}
              {isExpanded && detail && (
                <div className="mp-order-detail">
                  <div className="mp-detail-section">
                    <h4>{t("mypage.orders.orderInfo")}</h4>
                    <div className="mp-detail-grid">
                      <span>{t("mypage.orders.orderNumber")}</span><span>{detail.order_number || detail.id}</span>
                      <span>{t("mypage.orders.customerName")}</span><span>{detail.customer_name}</span>
                      {detail.customer_email && <><span>{t("mypage.orders.customerEmail", "이메일")}</span><span>{detail.customer_email}</span></>}
                      {detail.memo && <><span>{t("mypage.orders.memo")}</span><span>{detail.memo}</span></>}
                    </div>
                  </div>

                  {Array.isArray(detail.items) && detail.items.length > 0 && (
                    <div className="mp-detail-section">
                      <h4>{t("mypage.orders.items", "주문 항목")}</h4>
                      <table className="mp-items-table" style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#F4EADB" }}>
                            <th style={{ padding: 6, textAlign: "left" }}>#</th>
                            <th style={{ padding: 6, textAlign: "left" }}>{t("mypage.orders.fileName", "파일")}</th>
                            <th style={{ padding: 6 }}>{t("mypage.orders.color")}</th>
                            <th style={{ padding: 6 }}>{t("mypage.orders.quantity")}</th>
                            <th style={{ padding: 6, textAlign: "right" }}>{t("mypage.orders.price", "금액")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.items.map((it, i) => (
                            <tr key={i} style={{ borderTop: "1px solid #E7DAC8" }}>
                              <td style={{ padding: 6 }}>{i + 1}</td>
                              <td style={{ padding: 6 }}>{it.fileName}</td>
                              <td style={{ padding: 6, textAlign: "center" }}>{it.color}</td>
                              <td style={{ padding: 6, textAlign: "center" }}>{it.quantity}</td>
                              <td style={{ padding: 6, textAlign: "right" }}>{(it.price || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mp-detail-section">
                    <h4>{t("mypage.orders.shippingInfo")}</h4>
                    <div className="mp-detail-grid">
                      <span>{t("mypage.orders.shippingType")}</span><span>{detail.shipping_type === "domestic" ? t("mypage.orders.domestic") : t("mypage.orders.international")}</span>
                      {detail.zipcode && <><span>{t("mypage.orders.zipcode")}</span><span>{detail.zipcode}</span></>}
                      <span>{t("mypage.orders.address")}</span><span>{detail.address} {detail.address_detail}</span>
                      {detail.delivery_memo && <><span>{t("mypage.orders.deliveryMemo")}</span><span>{detail.delivery_memo}</span></>}
                    </div>
                  </div>

                  <div className="mp-detail-section">
                    <h4>{t("mypage.orders.paymentInfo")}</h4>
                    <div className="mp-detail-grid">
                      <span>{t("mypage.orders.printCost")}</span><span>{detail.total_price ? `${detail.total_price.toLocaleString()}${t("mypage.orders.currencyWon")}` : "-"}</span>
                      <span>{t("mypage.orders.shippingCost")}</span><span>{detail.shipping_cost ? `${detail.shipping_cost.toLocaleString()}${t("mypage.orders.currencyWon")}` : "-"}</span>
                      <span>{t("mypage.orders.totalPaid")}</span><span className="mp-detail-price">{((detail.total_price || 0) + (detail.shipping_cost || 0)).toLocaleString()}{t("mypage.orders.currencyWon")}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}

      {/* === 취소 모달 === */}
      {cancelModal && (
        <CancelModal
          order={cancelModal.order}
          mode={cancelModal.type}
          onClose={() => setCancelModal(null)}
          onInstantConfirm={(reason) => confirmInstantCancel(cancelModal.order, reason)}
          onRequestSubmit={() => { setCancelModal(null); reload(); }}
          userId={user?.id}
          t={t}
        />
      )}

      {/* === 반품/교환 모달 === */}
      {returnModal && (
        <ReturnRequestModal
          order={returnModal.order}
          requestType={returnModal.type}
          onClose={() => setReturnModal(null)}
          onSubmit={() => { setReturnModal(null); reload(); }}
          userId={user?.id}
          t={t}
        />
      )}

      {/* === 배송지 수정 모달 === */}
      {addressModal && (
        <EditShippingModal
          order={addressModal}
          onClose={() => setAddressModal(null)}
          onSaved={() => { setAddressModal(null); reload(); }}
          t={t}
        />
      )}

      {/* === 알림 모달 === */}
      {alertModal && (
        <AlertModal
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal(null)}
          t={t}
        />
      )}
    </div>
  );
}

// ============================================================
// 알림 모달
// ============================================================
function AlertModal({ message, type, onClose, t }) {
  const icon = type === "success" ? "✓"
    : type === "error" ? "✕"
    : type === "warning" ? "!"
    : "ℹ";
  const color = type === "success" ? "#6F8A5B"
    : type === "error" ? "#B4543A"
    : type === "warning" ? "#C8813F"
    : "#5A3825";

  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-alert-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mp-alert-icon" style={{ background: color }}>{icon}</div>
        <div className="mp-alert-message">{message}</div>
        <button className="mp-alert-btn" style={{ background: color }} onClick={onClose}>
          {t("common.confirm") || "확인"}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// 취소 모달 (즉시 취소 / 취소 신청 둘 다 처리)
// ============================================================
function CancelModal({ order, mode, onClose, onInstantConfirm, onRequestSubmit, userId, t }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!reason.trim()) {
      notify(t("mypage.orders.cancelReasonRequired"), "warning");
      return;
    }
    setSubmitting(true);
    if (mode === "instant") {
      await onInstantConfirm(reason);
      setSubmitting(false);
    } else {
      try {
        const res = await fetch(`${API}/api/returns`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            order_id: order.id,
            user_id: userId,
            request_type: "cancel",
            reason,
            detail: "",
          }),
        });
        const data = await res.json();
        if (res.ok) {
          notify(t("mypage.orders.cancelRequestSuccess"), "success");
          onRequestSubmit();
        } else {
          notify(data.error || t("mypage.orders.cancelFailed"), "error");
        }
      } catch (e) {
        notify(t("mypage.orders.cancelFailed"), "error");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>{mode === "instant" ? t("mypage.orders.cancelOrder") : t("mypage.orders.cancelRequest")}</h3>
          <button className="mp-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mp-modal-body">
          <p className="mp-modal-desc">
            {mode === "instant"
              ? t("mypage.orders.instantCancelDesc")
              : t("mypage.orders.cancelRequestDesc")}
          </p>
          <label className="mp-modal-label">
            {t("mypage.orders.cancelReason")}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("mypage.orders.cancelReasonPlaceholder")}
              rows={4}
            />
          </label>
        </div>
        <div className="mp-modal-actions">
          <button className="mp-modal-cancel-btn" onClick={onClose}>{t("common.cancel")}</button>
          <button className="mp-modal-submit-btn mp-modal-danger" onClick={submit} disabled={submitting}>
            {submitting ? t("mypage.orders.processing") : t("mypage.orders.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 반품/교환 신청 모달
// ============================================================
const REASON_OPTIONS = [
  { key: "defect", label: "상품 불량" },
  { key: "damaged", label: "파손" },
  { key: "wrongItem", label: "오배송" },
  { key: "other", label: "기타" },
];

function ReturnRequestModal({ order, requestType, onClose, onSubmit, userId, t }) {
  const [reasonKey, setReasonKey] = useState("");
  const [detail, setDetail] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleFileChange(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 5) {
      notify(t("mypage.orders.maxPhotosError"), "warning");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("user_id", userId);
        const res = await fetch(`${API}/api/returns/upload-photo`, {
          method: "POST",
          body: fd,
        });
        const data = await res.json();
        if (data.url) uploaded.push(data.url);
      }
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch (e) {
      notify(t("mypage.orders.uploadFailed"), "error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  function removePhoto(idx) {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!reasonKey) {
      notify(t("mypage.orders.reasonRequired"), "warning");
      return;
    }
    if (!detail.trim()) {
      notify(t("mypage.orders.detailRequired"), "warning");
      return;
    }
    setSubmitting(true);
    try {
      const reasonLabel = t(`mypage.orders.reasons.${reasonKey}`);
      const res = await fetch(`${API}/api/returns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: order.id,
          user_id: userId,
          request_type: requestType,
          reason: reasonLabel,
          detail,
          photos,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        notify(t("mypage.orders.requestSuccess"), "success");
        onSubmit();
      } else {
        notify(data.error || t("mypage.orders.requestFailed"), "error");
      }
    } catch (e) {
      notify(t("mypage.orders.requestFailed"), "error");
    }
    setSubmitting(false);
  }

  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-modal mp-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>{requestType === "return" ? t("mypage.orders.returnRequest") : t("mypage.orders.exchangeRequest")}</h3>
          <button className="mp-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mp-modal-body">
          <label className="mp-modal-label">
            {t("mypage.orders.reason")}
            <CSSelect
              block
              value={reasonKey}
              onChange={setReasonKey}
              placeholder={t("mypage.orders.selectReason")}
              options={REASON_OPTIONS.map((r) => ({
                value: r.key,
                label: t(`mypage.orders.reasons.${r.key}`),
              }))}
            />
          </label>

          <label className="mp-modal-label">
            {t("mypage.orders.detail")}
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={t("mypage.orders.detailPlaceholder")}
              rows={4}
            />
          </label>

          <label className="mp-modal-label">
            {t("mypage.orders.photos")} ({photos.length}/5)
            <div className="mp-photo-list">
              {photos.map((url, i) => (
                <div key={i} className="mp-photo-item">
                  <img src={url} alt="" />
                  <button onClick={() => removePhoto(i)}>✕</button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="mp-photo-add">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  {uploading ? "..." : "+"}
                </label>
              )}
            </div>
            <p className="mp-modal-hint">{t("mypage.orders.photosHint")}</p>
          </label>
        </div>
        <div className="mp-modal-actions">
          <button className="mp-modal-cancel-btn" onClick={onClose}>{t("common.cancel")}</button>
          <button className="mp-modal-submit-btn" onClick={submit} disabled={submitting || uploading}>
            {submitting ? t("mypage.orders.processing") : t("mypage.orders.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 배송지 수정 모달
// ============================================================
function EditShippingModal({ order, onClose, onSaved, t }) {
  const [form, setForm] = useState({
    customer_name: order.customer_name || "",
    customer_phone: order.customer_phone || "",
    zipcode: order.zipcode || "",
    address: order.address || "",
    address_detail: order.address_detail || "",
    delivery_memo: order.delivery_memo || "",
  });
  const [saving, setSaving] = useState(false);

  function openDaumPostcode() {
    if (!window.daum || !window.daum.Postcode) {
      notify(t("mypage.orders.daumNotLoaded"), "warning");
      return;
    }
    new window.daum.Postcode({
      oncomplete(data) {
        setForm((p) => ({
          ...p,
          zipcode: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        }));
      },
    }).open();
  }

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/orders/${order.id}/shipping`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        notify(t("mypage.orders.shippingUpdated"), "success");
        onSaved();
      } else {
        notify(data.error || t("mypage.orders.shippingUpdateFailed"), "error");
      }
    } catch (e) {
      notify(t("mypage.orders.shippingUpdateFailed"), "error");
    }
    setSaving(false);
  }

  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>{t("mypage.orders.editShipping")}</h3>
          <button className="mp-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mp-modal-body">
          <label className="mp-modal-label">
            {t("mypage.orders.customerName")}
            <input value={form.customer_name} onChange={(e) => setForm((p) => ({ ...p, customer_name: e.target.value }))} />
          </label>
          <label className="mp-modal-label">
            {t("mypage.orders.customerPhone")}
            <input value={form.customer_phone} onChange={(e) => setForm((p) => ({ ...p, customer_phone: e.target.value }))} />
          </label>
          <label className="mp-modal-label">
            {t("mypage.orders.zipcode")}
            <div style={{ display: "flex", gap: 6 }}>
              <input value={form.zipcode} readOnly style={{ flex: 1 }} />
              <button type="button" className="mp-modal-zip-btn" onClick={openDaumPostcode}>{t("mypage.address.searchZip")}</button>
            </div>
          </label>
          <label className="mp-modal-label">
            {t("mypage.orders.address")}
            <input value={form.address} readOnly />
          </label>
          <label className="mp-modal-label">
            {t("mypage.address.addressDetail")}
            <input value={form.address_detail} onChange={(e) => setForm((p) => ({ ...p, address_detail: e.target.value }))} />
          </label>
          <label className="mp-modal-label">
            {t("mypage.address.deliveryMemo")}
            <input value={form.delivery_memo} onChange={(e) => setForm((p) => ({ ...p, delivery_memo: e.target.value }))} />
          </label>
        </div>
        <div className="mp-modal-actions">
          <button className="mp-modal-cancel-btn" onClick={onClose}>{t("common.cancel")}</button>
          <button className="mp-modal-submit-btn" onClick={save} disabled={saving}>
            {saving ? t("mypage.orders.processing") : t("mypage.orders.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
